import { Form } from 'formik';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UPDATE_TOTAL_PRICE } from '../../../../../constants/actions/registerShipmentFormActions';
import { OPTIONAL_SERVICE_COD, OPTIONAL_SERVICE_INSURANCE } from '../../../../../constants/data/registerShipmentData';
import { getCurrentCulture, getCurrentLangPretty } from '../../../../../helpers/url/urlHelpers';
import usePostalServices from '../../../../../hooks/usePostalServices';
import useScrollToFirstFormikError from '../../../../../hooks/useScrollToFIrstFormikError';
import { ReactComponent as IconParcel } from '../../../../../icons/parcel.svg';
import { ReactComponent as IconMailOpen } from '../../../../../icons/tags.svg';
import IPostService from '../../../../../interfaces/forms/postService';
import Button from '../../../../base/button';
import FormikFieldWrapper from '../../../../base/forms/formikFieldWrapper';
import FormikNumberInput from '../../../../base/forms/formikNumberInput';
import FormikRadioTile from '../../../../base/forms/formikRadioTile/formikRadioTile';
import { Label } from '../../../../base/forms/label';
import { Column, Columns } from '../../../../base/grid';
import { A } from '../../../../base/typography';
import CalcPackageDimensions from '../../../../registerShipmentCalculator/calcForm/calcPackageDimensions/calcPackageDimensions';
import * as StyledStepType from '../../stepType/stepType.styles';
import { IStepShipmentFormInfo } from '../stepShipment.schema';
import Services from './components/services';
import WeightDropdown, { getWeightOptions } from './components/weightDropdown';
import { WEIGHT_CATEGORY } from './constants/weightCategories';
import * as StyledStepShipmentForm from './stepShipmentForm.styles';

interface Props {
	values: IStepShipmentFormInfo;
	setFieldTouched: any;
	setFieldValue: any;
	isValid: boolean;
	isSubmitting: boolean;
	onPrevious: any;
	errors: any;
}

const StepShipmentForm: FC<Props> = ({
	values,
	setFieldValue,
	setFieldTouched,
	onPrevious,
	isValid,
	isSubmitting,
	errors,
}) => {
	const ref = useRef(null);
	const dispatch = useDispatch<any>();
	const [customWeight, setCustomWeight] = useState(
		values.weightCategory === WEIGHT_CATEGORY.HEAVY && values.parcelWeight > 0 ? String(values.parcelWeight) : ''
	);
	const previousServiceRef = useRef(values.service);
	const { stepInfoFormInfo, insuranceOptionalServiceCustomPrice, codOptionalServiceCustomPrice } = useSelector(
		({ registerShipmentReducer }) => registerShipmentReducer
	);
	const { t } = useTranslation('registerShipment');
	const {
		services,
		servicesLoading,
	}: {
		services: IPostService[];
		servicesLoading: boolean;
	} = usePostalServices(
		values.parcelWeight,
		values.service,
		stepInfoFormInfo.country,
		stepInfoFormInfo.senderPostCode,
		stepInfoFormInfo.receiverPostCode,
		values.parcelLength,
		values.parcelWidth,
		values.parcelHeight
	);

	useScrollToFirstFormikError(errors, isSubmitting, isValid, ref);
	const lang = getCurrentCulture();
	useEffect(() => {
		let service: IPostService = null;
		if (services && values.serviceOption) {
			service = services.find(item => item.id === values.serviceOption);
			if (service === undefined) {
				setFieldValue('serviceName', null);
			} else if (values.serviceName !== service.labels[getCurrentLangPretty() === 'is' ? 0 : 1]?.title) {
				setFieldValue('serviceName', service.labels[getCurrentLangPretty() === 'is' ? 0 : 1]?.title || '');
			}
		}
	}, [services, values, setFieldValue]);

	useEffect(() => {
		if (values.dontKnowWeight && values.parcelWeight !== 0) {
			setFieldValue('parcelWeight', 0);
		}
	}, [values.dontKnowWeight, values.parcelWeight, setFieldValue]);

	useEffect(() => {
		const previousService = previousServiceRef.current;

		if (previousService !== values.service) {
			previousServiceRef.current = values.service;
			setFieldValue('weightCategory', '');
			setFieldValue('parcelWeight', 0);
			setFieldValue('dontKnowWeight', false);
			setCustomWeight('');
		}
	}, [values.service, setFieldValue]);

	useEffect(() => {
		if (!servicesLoading && services !== null && services.length === 1) {
			setFieldValue('serviceOption', services[0].id);
		}
	}, [servicesLoading, services, setFieldValue]);

	useEffect(() => {
		if (services && values.optionalServices) {
			const newOptionalServicesNames = [];
			if (values.optionalServicesNames.length !== values.optionalServices.length) {
				services.forEach(service => {
					if (service.id === values.serviceOption)
						service.extraServices.forEach(optionalService => {
							if (values.optionalServices.includes(optionalService.id)) {
								newOptionalServicesNames.push(
									optionalService.labels[getCurrentLangPretty() === 'is' ? 0 : 1].title
								);
							}
						});
					setFieldValue('optionalServicesNames', newOptionalServicesNames);
				});
			}
		}
	}, [services, values, setFieldValue]);

	useEffect(() => {
		services?.forEach(service => {
			if (service.id === values.serviceOption) {
				const allServices = [];
				const newServices = [];
				service.extraServices.forEach(optionalService => allServices.push(optionalService.id));
				values.optionalServices.forEach(currentOS => {
					if (allServices.includes(currentOS)) newServices.push(currentOS);
				});
				values.optionalServices = newServices;
			}
		});

		// eslint-disable-next-line
	}, [values.serviceOption]);

	const calcTotalPrice = (servicePrice, id) => {
		let extraServicesTotal = 0;

		services.forEach(service => {
			if (service.id === id) {
				service.extraServices.forEach(extraService => {
					if (values.optionalServices.includes(extraService.id))
						if (extraService.id === OPTIONAL_SERVICE_COD)
							extraServicesTotal += Math.max(extraService.priceAmount, codOptionalServiceCustomPrice);
						else if (extraService.id === OPTIONAL_SERVICE_INSURANCE)
							extraServicesTotal += Math.max(
								extraService.priceAmount,
								insuranceOptionalServiceCustomPrice
							);
						else extraServicesTotal += extraService.priceAmount;
				});
			}
		});

		const total = servicePrice + extraServicesTotal;

		return total.toString();
	};

	return (
		<div ref={ref}>
			<Form>
				<StyledStepShipmentForm.Wrapper>
					<StyledStepType.FieldColumns>
						<Column>
							<FormikFieldWrapper
								label={`${t('step-type.what-are-you-delivering')}?`}
								name="service"
								languageComponent="registerShipment">
								<Columns>
									<Column desktopSmall={1 / 2}>
										<FormikRadioTile
											id="Parcel"
											name="service"
											icon={IconParcel}
											active={false}
											setFieldValue={setFieldValue}
											label={t('step-type.parcel')}
											renderTopRight={() =>
												stepInfoFormInfo.country.value === 'IS' && (
													<FormikNumberInput
														id="parcelCount"
														name="parcelCount"
														onChange={setFieldValue}
														value={values.parcelCount}
														disabledText={t('step-type.parcel-disabled-text')}
														label={t('step-type.number-of-parcels')}
													/>
												)
											}
										/>
									</Column>
									<Column desktopSmall={1 / 2}>
										<FormikRadioTile
											id="Letter"
											name="service"
											icon={IconMailOpen}
											active={false}
											setFieldValue={setFieldValue}
											label={t('step-type.registered-letter')}
										/>
									</Column>
								</Columns>
							</FormikFieldWrapper>
						</Column>

						{stepInfoFormInfo.country.value !== 'IS' && (
							<Column>
								<A
									href={
										lang === 'is'
											? '/einstaklingar/senda/#Algengar-spurningar'
											: '/en/iceland-post/information/terms-conditions/?id=Contents'
									}
									target="_blank">
									{t('step-shipment.foodWarning')}
								</A>
							</Column>
						)}
					</StyledStepType.FieldColumns>
					{values.parcelCount > 1 && services?.some(e => e.id === 'Postbox') && (
						<StyledStepType.InfoWrapper>{t('step-type.moreThan1')}</StyledStepType.InfoWrapper>
					)}
					<Label marginBottom="1rem">{t('step-shipment.choose-weight')}</Label>
					<StyledStepShipmentForm.WeightEstimateInfo>
						{t('step-shipment.choose-weight-info')}
					</StyledStepShipmentForm.WeightEstimateInfo>
					<FormikFieldWrapper
						name="weightCategory"
						label={t('step-shipment.weight')}
						smallLabel
						languageComponent="registerShipment">
						<WeightDropdown
							service={values.service}
							countryCode={stepInfoFormInfo.country.value}
							selectedValue={values.weightCategory}
							onChange={(weightCategory, parcelWeight, dontKnowWeight) => {
								setFieldValue('weightCategory', weightCategory);
								setFieldValue('parcelWeight', parcelWeight);
								setFieldValue('dontKnowWeight', dontKnowWeight);
								if (weightCategory !== WEIGHT_CATEGORY.HEAVY) {
									setCustomWeight('');
								}
							}}
							onBlur={setFieldTouched}
							customWeight={customWeight}
							onCustomWeightChange={(val) => {
								setCustomWeight(val);
								const numVal = parseFloat(val.replace(',', '.'));
								setFieldValue('parcelWeight', numVal >= 0 ? numVal : 0);
							}}
						/>
					</FormikFieldWrapper>
					{values.service === 'Parcel' && (
						<StyledStepShipmentForm.VolumeSection>
							<Label small>{t('step-shipment.volume-question')}</Label>
							<CalcPackageDimensions
								width={values.parcelWidth}
								height={values.parcelHeight}
								length={values.parcelLength}
								infoText={t('step-shipment.volume-info')}
							/>
						</StyledStepShipmentForm.VolumeSection>
					)}
					<Label>{t('step-shipment.choose-service')}</Label>
					<Services
						services={services}
						loading={servicesLoading}
						values={values}
						errors={errors}
						setFieldTouched={setFieldTouched}
						setFieldValue={setFieldValue}
						parcelCount={values.parcelCount}
						weightCategoryLabel={
							values.weightCategory === WEIGHT_CATEGORY.HEAVY
								? `${values.parcelWeight} kg`
								: values.weightCategory === WEIGHT_CATEGORY.UNKNOWN || !values.weightCategory
								? getWeightOptions(values.service, stepInfoFormInfo.country.value, t)[0]?.label
								: getWeightOptions(values.service, stepInfoFormInfo.country.value, t)
									.find(o => o.value === values.weightCategory)?.label
						}
					/>
					<StyledStepShipmentForm.ButtonWrapper>
						<Button type="button" onClick={onPrevious} wide>
							{t('shared.previous-step')}
						</Button>
						<Button
							wide
							primary
							type="submit"
							onClick={e => {
								if (values.serviceOption === 'Postbox' && values.parcelCount > 1) {
									e.preventDefault();
									return;
								}
								const service = services?.find(x => x.id === values.serviceOption);
								if (service)
									dispatch({
										type: UPDATE_TOTAL_PRICE,
										payload: { price: calcTotalPrice(service.priceAmount, service.id) },
									});
							}}>
							{t('shared.next-step')}
						</Button>
					</StyledStepShipmentForm.ButtonWrapper>
				</StyledStepShipmentForm.Wrapper>
			</Form>
		</div>
	);
};

export default StepShipmentForm;
